import React, { useState, useEffect, useRef, useCallback } from "react";
import { Oval as Loader } from "react-loader-spinner";
import "./report-modal.css";
import { showError } from "../../../shared/utils/swalHandler.js";
import { USER_TAG } from "../../../shared/constants/valisdation.js";
import { searchContentCreators } from "../../../shared/actions/contentAction.js";
import { reportContentCreator } from "../../../shared/actions/helpAction.js";
import { useModalOpen } from "../../../shared/utils/modalUtils.js";
import { createPortal } from "react-dom";

/**
 * ImpersonationModal Component
 * 
 * Modal for reporting a profile for impersonating another content creator
 * 
 * @param {boolean} modalOpen - Controls visibility of the modal
 * @param {Function} onClose - Callback function called when modal is closed
 * @param {string} creatorId - ID of the creator being reported
 */
const ImpersonationModal = ({ modalOpen, onClose, creatorId }) => {
  const [modal, setModal] = useState(modalOpen);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [profiles, setProfiles] = useState([]);
  const [searchInput, setSearchInput] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);

  const searchTimeout = useRef(null);
  const modalContainerRef = useRef(null);

  // Update modal state when prop changes
  useEffect(() => {
    setModal(modalOpen);
  }, [modalOpen]);

  // Add/remove class on HTML tag when modal opens/closes
  useModalOpen(modal);

  // Focus modal when it opens
  useEffect(() => {
    if (modal && modalContainerRef.current) {
      modalContainerRef.current.focus();
      modalContainerRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [modal]);

  // Reset search when modal closes
  useEffect(() => {
    if (!modal) {
      setSearchInput("");
      setProfiles([]);
      setIsSubmitted(false);
      setReportLoading(false);
      clearTimeout(searchTimeout.current);
    }
  }, [modal]);

  // Search for content creators
  const searchCreators = useCallback(async (query) => {
    if (!query || query.trim().length < USER_TAG.MIN_SEARCH_LENGTH) {
      setProfiles([]);
      return;
    }

    setSearchLoading(true);
    try {
      const result = await searchContentCreators(query, {
        limit: 8,
        skip: 0,
        sortBy: 'username_asc'
      });

      if (result.success && result.data) {
        // Check if result.data is an array or has a nested data array
        const dataArray = Array.isArray(result.data)
          ? result.data
          : (result.data.data && Array.isArray(result.data.data))
            ? result.data.data
            : [];

        if (dataArray.length > 0) {
          // Exclude the creator being reported from impersonation targets
          const filteredProfiles = creatorId
            ? dataArray.filter((profile) => {
                const profileId = profile._id || profile.id;
                return profileId !== creatorId;
              })
            : dataArray;

          setProfiles(filteredProfiles);
        } else {
          setProfiles([]);
        }
      } else {
        setProfiles([]);
        const errorMessage = result.error || "Something went wrong. Please try again.";
        await showError("Error", errorMessage);
      }
    } catch (error) {
      setProfiles([]);
      const errorMessage = error.response?.data?.message || error.message ||
        "Something went wrong. Please try again.";
      await showError("Error", errorMessage);
    } finally {
      setSearchLoading(false);
    }
  }, [creatorId]);

  // Handle search input change with debounce
  const handleSearchChange = useCallback((e) => {
    const value = e.target.value;
    setSearchInput(value);

    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      searchCreators(value);
    }, USER_TAG.SEARCH_DEBOUNCE_MS);
  }, [searchCreators]);

  // Handle modal close
  const handleClose = useCallback(() => {
    setModal(false);
    if (onClose) {
      onClose();
    }
  }, [onClose]);

  // Handle report submission
  const handleReport = useCallback(async (account) => {
    const reportData = {
      reason: "Impersonating another content creator",
      account_linked: account,
    };

    setReportLoading(true);
    try {
      const result = await reportContentCreator(creatorId, reportData);

      // Check both HTTP status and data status (if present) to match original behavior
      const isSuccess = result.success &&
        (result.status === 200 || result.data?.status === 200);

      if (isSuccess) {
        setIsSubmitted(true);
      } else {
        const errorMessage = result.error || "Something went wrong. Please try again.";

        if (result.status === 422) {
          await showError("Sorry!", errorMessage);
        } else {
          await showError("Error", errorMessage);
        }
      }
    } catch (error) {
      const errorMessage = error.message || "Something went wrong. Please try again.";
      await showError("Error!", errorMessage);
    } finally {
      setReportLoading(false);
    }
  }, [creatorId]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      clearTimeout(searchTimeout.current);
    };
  }, []);

  if (!modal) {
    return null;
  }

  return createPortal(
    <div className="report-modal-overlay cm_bg_blur">
      <div
        ref={modalContainerRef}
        className="report-modal-container"
        onClick={(e) => e.stopPropagation()}
        tabIndex={-1}
      >
        <div className="report-modal-header">
          <h3 className="report-modal-title">Impersonation</h3>
          <button
            type="button"
            className="report-modal-close"
            onClick={handleClose}
            aria-label="Close"
          >
            <i className="fa-solid fa-times"></i>
          </button>
        </div>

        <div className="report-modal-body">
          {isSubmitted ? (
            <div className="report-modal-success">
              <p className="report-modal-success-message">
                Thank you for helping to keep our community safe, we have received
                your report.
              </p>
            </div>
          ) : (
            <>
              <div className="report-modal-description">
                <p>Which content creator are they pretending to be?</p>
              </div>

              <div className="report-modal-search">
                <div className="report-modal-search-container">
                  <div className="report-modal-search-icon">
                    <i className="fa-solid fa-search"></i>
                  </div>
                  <input
                    type="text"
                    placeholder="Search content creators..."
                    className="report-modal-search-input"
                    value={searchInput}
                    onChange={handleSearchChange}
                    disabled={searchLoading}
                  />
                  {searchLoading && (
                    <div className="report-modal-search-loading">
                      <Loader
                        type="Oval"
                        color="#f534bf"
                        height={20}
                        width={20}
                        ariaLabel="Searching users"
                      />
                    </div>
                  )}
                </div>
              </div>

              {searchInput.length >= USER_TAG.MIN_SEARCH_LENGTH && (
                <div className="report-modal-user-list">
                  {reportLoading ? (
                    <div className="report-modal-loading">
                      <Loader
                        type="Oval"
                        color="#f534bf"
                        height={40}
                        width={40}
                        ariaLabel="Submitting report"
                      />
                      <p>Submitting report...</p>
                    </div>
                  ) : profiles && profiles.length > 0 ? (
                    profiles.map((profile) => {
                      const uniqueKey = profile.username || profile._id || profile.id;
                      return (
                        <div
                          key={uniqueKey}
                          className="report-modal-user-item"
                        >
                          <img
                            src={profile.profile_picture || USER_TAG.DEFAULT_AVATAR}
                            alt={profile.username || "User"}
                            className="report-modal-user-avatar"
                          />
                          <div className="report-modal-user-info">
                            <div className="report-modal-user-username">@{profile.username}</div>
                          </div>
                          <button
                            type="button"
                            className="report-modal-user-button"
                            onClick={() => handleReport(profile.username)}
                            disabled={reportLoading}
                          >
                            Report
                          </button>
                        </div>
                      );
                    })
                  ) : (
                    <div className="report-modal-no-results">
                      <p>No users found</p>
                    </div>
                  )}
                </div>
              )}

              {searchInput.length > 0 && searchInput.length < USER_TAG.MIN_SEARCH_LENGTH && (
                <div className="report-modal-no-results">
                  <p>Type at least {USER_TAG.MIN_SEARCH_LENGTH} characters to search</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>, document.body
  );
};

export default ImpersonationModal;
